import { useMemo } from 'react';
import type { DeliveryAddress } from '../../types';
import Icon from '../../ui-kit/Icon';

interface SavedAddressQuickFillProps {
  addresses: DeliveryAddress[];
  title: string;
  noneLabel: string;
  selectedAddressId: number | null;
  onSelect: (address: DeliveryAddress | null) => void;
}

function truncateLabel(value: string, maxLength: number): string {
  const normalized = value.trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 1)}...`;
}

function buildOptionLabel(address: DeliveryAddress): string {
  const baseLabel = truncateLabel(address.label?.trim() || address.address, 40);
  const defaultLabel = address.isDefault ? ' ★' : '';
  const phone = address.phoneNumber ? ` — ${address.phoneNumber}` : '';
  return `${baseLabel}${defaultLabel}${phone}`;
}

export default function SavedAddressQuickFill({
  addresses,
  title,
  noneLabel,
  selectedAddressId,
  onSelect,
}: SavedAddressQuickFillProps) {
  const addressById = useMemo(
    () => new Map(addresses.map((address) => [address.id, address])),
    [addresses],
  );

  return (
    <label className="flex flex-col gap-2">
      <span className="text-[11px] font-bold uppercase tracking-wider text-text-secondary px-1">
        {title}
      </span>
      <div className="relative">
        <select
          value={selectedAddressId === null ? '' : String(selectedAddressId)}
          onChange={(event) => {
            const rawValue = event.target.value;
            if (!rawValue) {
              onSelect(null);
              return;
            }
            const parsedId = Number.parseInt(rawValue, 10);
            const selected = Number.isInteger(parsedId) ? addressById.get(parsedId) : null;
            onSelect(selected ?? null);
          }}
          className="w-full h-14 bg-primary border-none rounded-xl px-4 text-text font-sans text-base focus:bg-primary focus:shadow-[0_0_0_2px_var(--color-focus-ring)] transition-all outline-none appearance-none"
        >
          <option value="">{noneLabel}</option>
          {addresses.map((address) => (
            <option key={address.id} value={String(address.id)}>
              {buildOptionLabel(address)}
            </option>
          ))}
        </select>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary">
          <Icon name="expand_more" />
        </div>
      </div>
    </label>
  );
}
